import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export const AnalysisSequence = ({ onDone, active }) => {
  const { t } = useI18n();
  const steps = t("analysis.steps");
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const perStep = 5500;
    const start = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const p = Math.min(1, elapsed / (perStep * steps.length));
      setProgress(p);
      const idx = Math.min(steps.length - 1, Math.floor(elapsed / perStep));
      setCurrent(idx);
      if (p >= 1 && !cancelled) {
        clearInterval(timer);
      }
    }, 100);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [active, steps.length]);

  useEffect(() => {
    if (progress >= 1) onDone?.();
    // eslint-disable-next-line
  }, [progress]);

  return (
    <div data-testid="analysis-sequence" className="min-h-[70vh] grid place-items-center px-6">
      <div className="max-w-2xl w-full">
        <div className="eyebrow mb-6 text-center">Impact Audit AI</div>
        <div className="relative w-full h-1 bg-[color:var(--impact-border)] rounded-full overflow-hidden mb-12">
          <div className="h-full bg-[color:var(--impact-ink)] transition-[width] duration-500" style={{ width: `${progress * 100}%` }} />
        </div>
        <ul className="space-y-4">
          {steps.map((s, i) => {
            const done = i < current || progress >= 1;
            const isCurrent = i === current && progress < 1;
            return (
              <motion.li
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 text-lg"
                data-testid={`analysis-step-${i}`}
              >
                {done ? (
                  <CheckCircle2 size={22} className="text-[color:var(--impact-low)]" />
                ) : (
                  <span className={`w-5 h-5 rounded-full border-2 ${isCurrent ? "border-[color:var(--impact-ink)] border-t-transparent animate-spin" : "border-[color:var(--impact-border)]"}`} />
                )}
                <span className={done ? "text-[color:var(--impact-ink)]" : isCurrent ? "text-[color:var(--impact-ink)] font-semibold" : "text-[color:var(--impact-ink-2)]"}>
                  {s}
                </span>
              </motion.li>
            );
          })}
        </ul>
        <div className="mt-10 text-center text-sm text-[color:var(--impact-ink-2)]">{t("analysis.hint")}</div>
      </div>
    </div>
  );
};
